// LoginModal.jsx (React-Bootstrap Version)
import React, { useEffect, useRef, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import API from "../config/axios";
import { api_paths } from "../config/api.paths";
import { SET_LOADING, SET_USER } from "../redux/user";
import { toastErrorMessage, toastSuccessMessage } from "../utils/utils";
import { clearError, validateData } from "../validators/YupValidations";
import { loginSchema } from "../validators/validatorSchema";
import ForgotPassword from "./auth/ForgotPassword";

const LoginModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({});
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const emailRef = useRef(null);
  const [isForgotOpen, setIsForgotOpen] = useState(false);

  // Autofocus when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => emailRef.current?.focus(), 150);
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isValid = await validateData(loginSchema, formData, setErrors);
    if (!isValid) return;

    try {
      dispatch(SET_LOADING(true));

      const response = await API.post(api_paths.auth.LOGIN, formData);

      if (response.data?.payload?.result) {
        const result = response.data.payload.result;
        dispatch(SET_USER(result.memberDoc));
        localStorage.setItem("GSTB_token", result.token);
        API.defaults.headers.common["Authorization"] = `Bearer ${result.token}`;

        toastSuccessMessage(response?.data?.message);

        onClose(); // Close modal
        navigate("/"); // Redirect
      } else {
        toastErrorMessage(response?.data?.message || "Login failed");
      }
    } catch (error) {
      toastErrorMessage(error?.response?.data?.message || "Something went wrong");
    } finally {
      dispatch(SET_LOADING(false));
    }
  };

  const goToRegister = () => {
    onClose();
    setTimeout(() => navigate("/register"), 200);
  };

  return (
    <>
      <Modal show={isOpen} onHide={onClose} centered backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title className="fw-semibold">Login</Modal.Title>
        </Modal.Header>

        <Modal.Body className="px-4">
          <Form onSubmit={handleSubmit} noValidate>
            {/* Email */}
            <Form.Group className="mb-3">
              <Form.Label className="small">Email</Form.Label>
              <Form.Control
                ref={emailRef}
                type="email"
                placeholder="Enter your email"
                className={`p-3 ${errors.email ? "is-invalid" : ""}`}
                value={formData.email}
                onChange={(e) => {
                  setFormData({ ...formData, email: e.target.value });
                  errors.email && clearError(setErrors, "email");
                }}
              />
              {errors.email && <div className="invalid-feedback">{errors.email.message}</div>}
            </Form.Group>

            {/* Password */}
            <Form.Group className="mb-3">
              <Form.Label className="small">Password</Form.Label>
              <Form.Control
                type="password"
                placeholder="Enter your password"
                className={`p-3 ${errors.password ? "is-invalid" : ""}`}
                value={formData.password}
                onChange={(e) => {
                  setFormData({ ...formData, password: e.target.value });
                  errors.password && clearError(setErrors, "password");
                }}
              />
              {errors.password && <div className="invalid-feedback">{errors.password.message}</div>}
            </Form.Group>

            <Button
              onClick={() => setIsForgotOpen(true)}
              variant="link"
              className="forgot-link text-decoration-none mb-3"
            >
              Forgot Password ?
            </Button>

            <Button type="submit" className="btn-orange-lg w-100 fw-semibold">
              Sign in
            </Button>

            <div className="text-center mt-3 mb-2">
              <span className="fw-medium no-account fw-medium">Don't Have An Account?</span>
              <span className="signup fw-normal ms-2 cursor-pointer" onClick={goToRegister}>
                Sign Up
              </span>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
      <ForgotPassword isOpen={isForgotOpen} onClose={() => setIsForgotOpen(false)} />
    </>
  );
};

export default LoginModal;
